import React, { useState, useTransition } from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { useTranslation } from "react-i18next";
import { toggleFavService } from "redux/services/general_service";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const ProductCard = ({ product, onAddToCart, onToggleWishlist }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const handleQuickAdd = async () => {
    if (product.variants && product.variants.length > 0 && !selectedVariant) {
      setShowQuickAdd(true);
      return;
    }

    setIsLoading(true);
    try {
      await onAddToCart(product, selectedVariant);
      setShowQuickAdd(false);
      setSelectedVariant(null);
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWishlistToggle = () => {
    onToggleWishlist(product.id);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(i18n.language === "en" ? "en" : "ar", {
      style: "currency",
      currency: "IQD",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const calculateDiscountPercentage = () => {
    if (!product.price || !product.price_after_discount) return 0;
    return Math.round(
      ((product.price - product.price_after_discount) / product.price) * 100
    );
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon
          key={i}
          name="Star"
          size={14}
          className="text-yellow-400 fill-current"
        />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon
          key="half"
          name="StarHalf"
          size={14}
          className="text-yellow-400 fill-current"
        />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon
          key={`empty-${i}`}
          name="Star"
          size={14}
          className="text-gray-300"
        />
      );
    }

    return stars;
  };

  const onToggleSelection = (e, id) => {
    e.stopPropagation();

    dispatch(toggleFavService({ product_id: id })).then((response) => {
      if (response.payload.status === 200) {
        window.location.reload();
      } else {
        toast.warn(t("sign_in_first"));
      }
    });
  };

  // Determine badge and badgeColor based on original logic (discount)
  const badge = product.has_discount ? `-${calculateDiscountPercentage()}%` : null;
  const badgeColor = product.has_discount ? 'accent' : null; // Assuming 'accent' for discount, adjust if needed

  // Truncate product name to 100 characters
  const truncatedName = product?.name.length > 100 ? product?.name.slice(0, 100) + '...' : product?.name;

  return (
    <>
      <div
        key={product?.id}
        className="bg-white rounded-xl shadow-card hover:shadow-lg transition-all duration-300 overflow-hidden border border-border group h-96 flex flex-col"
      >
        {/* Product Image */}
        <div className="relative h-48 overflow-hidden">
          <Link to={`/product-detail?id=${product.id}`}>
            <Image
              src={product.images[0]?.file_path || product.category.image}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </Link>

          {/* Badge */}
          {badge && (
            <div
              className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-cairo font-bold ${
                badgeColor === 'success' ? 'bg-success text-white' :
                badgeColor === 'primary' ? 'bg-primary text-white' :
                badgeColor === 'accent' ? 'bg-accent text-black' : 'bg-gray-500 text-white'
              }`}
            >
              {badge}
            </div>
          )}

          {/* Quick Actions */}
          <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button
              onClick={(e) => onToggleSelection(e, product.id)}
              className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
            >
              <Icon
                name="Heart"
                size={16}
                className={`${
                  product.in_favorite ? 'text-destructive fill-current' : 'text-text-secondary'
                } hover:text-destructive`}
              />
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-4 text-right flex-1 flex flex-col justify-between">
          <div>
            <h3 className="font-cairo font-bold text-text-primary mb-2 line-clamp-2">
              {truncatedName}
            </h3>

            {/* Rating */}
            <div className="flex items-center justify-end gap-2 mb-3">
              <span className="text-sm text-text-secondary font-cairo">
                ({product?.reviews || 0})
              </span>
              <div className="flex items-center gap-1">
                <span className="text-sm font-bold text-text-primary">
                  {product?.rate}
                </span>
                <Icon name="Star" size={14} className="text-accent fill-current" />
              </div>
            </div>

            {/* Price */}
            <div className="flex items-center justify-end gap-2 mb-4">
              <span className="text-lg font-bold text-primary font-noto-arabic">
                {product?.price_after_discount !== null
                  ? formatPrice(product?.price_after_discount)
                  : formatPrice(product?.price)} ر.س
              </span>
              {product?.price_after_discount !== null &&
                product?.price > product?.price_after_discount && (
                  <span className="text-sm text-text-secondary line-through font-noto-arabic">
                    {formatPrice(product?.price)} ر.س
                  </span>
                )}
            </div>
          </div>

          {/* Add to Cart Button */}
          <Button
            variant="default"
            fullWidth
            size="sm"
            onClick={()=>navigate(`/product-detail?id=${product?.id}`)}
        //    onClick={handleQuickAdd}
            loading={isLoading}
            disabled={product.stock === 0}
            className="font-cairo font-semibold"
          >
            <Icon name="ShoppingCart" size={16} className="ml-2" />
            أضف للسلة
          </Button>
        </div>
      </div>

      {/* Quick Add Modal */}
      {showQuickAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-text-primary">
                  {t("selectOptions")}
                </h3>
                <button
                  onClick={() => setShowQuickAdd(false)}
                  className="text-text-secondary hover:text-text-primary transition-smooth"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-16 h-16 bg-muted rounded-md overflow-hidden flex-shrink-0">
                  <Image
                    src={product.images[0]?.file_path || product.category.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-text-primary line-clamp-2">
                    {product.category.translations?.en?.name}{" "}
                  </h4>
                  <h4 className="font-medium text-text-primary line-clamp-2">
                    {product.category.translations?.ar?.name}{" "}
                  </h4>
                  <p className="text-lg font-semibold text-text-primary font-mono mt-1">
                    {product.price_after_discount
                      ? formatPrice(product.price_after_discount)
                      : formatPrice(product.price)}
                  </p>
                </div>
              </div>

              {/* Variants */}
              {product?.variants && product?.variants.length > 0 && (
                <div className="space-y-4">
                  {product?.variants?.map((variant) => (
                    <div key={variant.id}>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        {variant.values[0].product_attribute_name}{" "}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {variant.values.map((option) => (
                          <button
                            key={option.id}
                            onClick={() =>
                              setSelectedVariant({
                                ...selectedVariant,
                                [variant.values[0].product_attribute_name]:
                                  option.value,
                              })
                            }
                            className={`px-3 py-2 text-sm border rounded-md transition-smooth ${
                              selectedVariant?.[
                                variant.values[0].product_attribute_name
                              ] === option.value
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border text-text-primary hover:border-primary"
                            }`}
                          >
                            {option.value}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/*<div className="flex space-x-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowQuickAdd(false)}
                  fullWidth
                >
                  {t("cancel")}
                </Button>
                <Button
                  variant="default"
                  onClick={handleQuickAdd}
                  loading={isLoading}
                  fullWidth
                  iconName="ShoppingCart"
                  iconPosition="left"
                >
                 {t("addToCart")}
                </Button>
              </div>*/}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
